import React, { useEffect, useState } from 'react';
import { getWeeklySeller } from '../services/apiService';
import '../styles/WeeklyCondorPanel.css';

// Weekly paper condor (WeeklySeller) — a separate book from the intraday engine, held to expiry.
// Polls the backend on its own: the condor changes slowly (stop is checked near the close only).
const POLL_MS = 30000;

const fmtRs = v => v == null ? '—' : `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;
const fmt2  = v => v == null ? '—' : Number(v).toFixed(2);
const fmtI  = v => v == null ? '—' : Math.round(v).toLocaleString('en-IN');
const day   = iso => iso ? String(iso).slice(0, 10) : '—';
const pnlColor = v => v == null ? '#e0e0e0' : v >= 0 ? '#00c853' : '#ef5350';

const OpenCondor = ({ p }) => {
  const shortCall = p.legs?.find(l => l.side === 'SELL' && l.right === 'call');
  const shortPut  = p.legs?.find(l => l.side === 'SELL' && l.right === 'put');
  const spot = p.lastSpot ?? p.entrySpot;
  // How far spot sits from each short strike, as % of spot — the cushion left before a leg goes in the money.
  const cushion = (strike, call) => strike && spot ? ((call ? strike - spot : spot - strike) / spot) * 100 : null;
  const cCall = cushion(shortCall?.strike, true);
  const cPut  = cushion(shortPut?.strike, false);
  const cushionColor = c => c == null ? '#e0e0e0' : c < 0 ? '#ef5350' : c < 0.5 ? '#ffb300' : '#00c853';
  // Share of the stop distance used: 0% at the entry credit, 100% at the stop debit.
  const stopUsed = p.lastDebit != null && p.stopDebit > p.credit
    ? Math.max(0, Math.min(100, ((p.lastDebit - p.credit) / (p.stopDebit - p.credit)) * 100)) : null;

  return (
    <div className="wc-open">
      <div className="wc-structure">
        <span>{p.structure}</span>
        <span className="wc-muted">exp {p.expiry}</span>
      </div>
      <div className="wc-grid">
        <div className="wc-item"><span className="wc-label">Credit</span><span className="wc-value">₹{fmt2(p.credit)} × {p.qty}</span></div>
        <div className="wc-item"><span className="wc-label">Cost to close</span><span className="wc-value">{p.lastDebit != null ? `₹${fmt2(p.lastDebit)}` : 'no mark yet'}</span></div>
        <div className="wc-item">
          <span className="wc-label">Unrealised</span>
          <span className="wc-value" style={{ color: pnlColor(p.unrealisedRs) }}>{fmtRs(p.unrealisedRs)}</span>
        </div>
        <div className="wc-item"><span className="wc-label">Stop at debit</span><span className="wc-value">₹{fmt2(p.stopDebit)}</span></div>
        <div className="wc-item">
          <span className="wc-label">Spot</span>
          <span className="wc-value">{fmtI(spot)} <span className="wc-muted">(entry {fmtI(p.entrySpot)})</span></span>
        </div>
        <div className="wc-item">
          <span className="wc-label">Cushion put / call</span>
          <span className="wc-value">
            <span style={{ color: cushionColor(cPut) }}>{cPut == null ? '—' : `${cPut.toFixed(2)}%`}</span>
            {' / '}
            <span style={{ color: cushionColor(cCall) }}>{cCall == null ? '—' : `${cCall.toFixed(2)}%`}</span>
          </span>
        </div>
        <div className="wc-item"><span className="wc-label">Worst / best debit</span><span className="wc-value">₹{fmt2(p.worstDebit)} / ₹{fmt2(p.bestDebit)}</span></div>
        <div className="wc-item"><span className="wc-label">Max loss</span><span className="wc-value">₹{fmtI(p.maxLossPts * p.qty)}</span></div>
      </div>
      {stopUsed != null && (
        <div className="wc-bar" title="How much of the way from the entry credit to the stop the cost to close has moved">
          <div className="wc-bar-fill" style={{ width: `${stopUsed}%`, background: stopUsed >= 75 ? '#ef5350' : stopUsed >= 40 ? '#ffb300' : '#00c853' }} />
          <span className="wc-bar-text">{stopUsed.toFixed(0)}% of the way to the stop</span>
        </div>
      )}
      <div className="wc-muted">Opened {day(p.opened)}{p.entryVix != null ? ` · VIX ${Number(p.entryVix).toFixed(1)}` : ''}</div>
    </div>
  );
};

const WeeklyCondorPanel = ({ username, symbol }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    const poll = () => getWeeklySeller(username)
      .then(d => { if (!cancelled) { setData(d); setError(false); } })
      .catch(() => { if (!cancelled) setError(true); }); // keep the last-known view on a transient failure
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [username]);

  if (!data) {
    return error ? null : <div className="wc-panel wc-muted">Weekly condor: loading…</div>;
  }

  // The condor runs on data.symbols only; on any other index say so instead of showing their book.
  const sym = symbol ? String(symbol).toUpperCase() : null;
  const traded = data.symbols || [];
  if (sym && traded.length && !traded.includes(sym)) {
    return (
      <div className="wc-panel wc-muted">
        Condor (paper) runs on {traded.join(', ')} only — not on {sym}.
      </div>
    );
  }
  const mineCfg = (sym && data.bySymbol?.[sym]) || null;
  const cycle = mineCfg?.cycle || 'Weekly';
  const rules = mineCfg?.rules || data.rules;
  const mine = x => !sym || x.symbol === sym;
  const open = (data.open || []).filter(mine);
  const history = (data.history || []).filter(mine);
  const s = sym && traded.length > 1 ? {
    trades: history.length,
    wins: history.filter(h => h.outcome === 'WIN').length,
    losses: history.filter(h => h.outcome !== 'WIN').length,
    netRs: history.reduce((a, h) => a + (h.netRs || 0), 0),
    avgPts: history.length ? history.reduce((a, h) => a + (h.pnlPts || 0), 0) / history.length : 0,
  } : (data.summary || {});
  const skips = Object.entries(data.lastSkip || {}).filter(([k]) => !sym || k === sym);

  return (
    <div className="wc-panel">
      <div className="wc-header">
        <span className="wc-title">{cycle} Condor <span className="wc-muted">(paper)</span></span>
        <span className="wc-badge" style={{ background: !data.enabled ? '#616161' : open.length ? '#1e88e5' : '#455a64' }}>
          {!data.enabled ? 'OFF' : open.length ? 'HOLDING' : 'FLAT'}
        </span>
      </div>
      <div className="wc-rules">{rules}</div>

      {open.map(p => <OpenCondor key={p.clientId} p={p} />)}
      {data.enabled && open.length === 0 && (
        <div className="wc-flat">
          No condor open.
          {skips.map(([sym, why]) => <div key={sym} className="wc-muted">{sym}: {why}</div>)}
        </div>
      )}

      <div className="wc-summary">
        <span>{s.trades ?? 0} closed · {s.wins ?? 0}W / {s.losses ?? 0}L</span>
        <span>avg {fmt2(s.avgPts)} pts</span>
        <span style={{ color: pnlColor(s.netRs) }}>net {fmtRs(s.netRs)}</span>
      </div>

      {history.length > 0 && (
        <table className="wc-table">
          <thead>
            <tr><th>Expiry</th><th>Credit</th><th>Exit</th><th>Pts</th><th>Net</th><th>Exit reason</th></tr>
          </thead>
          <tbody>
            {history.map(h => (
              <tr key={`${h.symbol}-${h.opened}`} title={h.structure}>
                <td>{h.expiry}</td>
                <td>{fmt2(h.credit)}</td>
                <td>{fmt2(h.exitDebit)}</td>
                <td style={{ color: pnlColor(h.pnlPts) }}>{fmt2(h.pnlPts)}</td>
                <td style={{ color: pnlColor(h.netRs) }}>{fmtRs(h.netRs)}</td>
                <td className="wc-reason">{h.exitReason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="wc-guide">
        Backtest (Jul 2024 – Oct 2026, close prices): {sym === 'BANKNIFTY'
          ? '16 monthly cycles, 81% won, about +₹45.8k per lot in all.'
          : 'about +9.6 pts a week.'} Separate from the intraday book — not in its caps, daily stop or 15:15 square-off.
      </div>
    </div>
  );
};

export default WeeklyCondorPanel;
